import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, IdCard, KeyRound, Loader2, Mail, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { ApiError, identityApi, type IdentityStartResponse, type Profile } from "@/lib/api";
import { Button } from "@/components/ui";
import { PersonNameFields } from "@/components/PersonNameFields";
import { emptyPersonName, profileFromApi, type PersonNameValue } from "@/lib/person-name";
import Logo from "@/components/layout/Logo";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { cn } from "@/lib/cn";

type DocType = Profile["documentType"];
type Step = "doc" | "code" | "done";

const DOC_TYPES: Array<{ id: DocType; label: string; helper: string }> = [
  { id: "CC", label: "Cédula de ciudadanía", helper: "Personas naturales" },
  { id: "CE", label: "Cédula de extranjería", helper: "Residentes extranjeros" },
  { id: "PASAPORTE", label: "Pasaporte", helper: "Persona natural extranjera" },
  { id: "NIT", label: "NIT", helper: "Personas jurídicas" },
];

export function Onboarding({ onComplete }: { onComplete: (profile: Profile) => void }) {
  const { t } = useT();
  const auth = useAuth();
  const { updateProfile } = useData();

  const [step, setStep] = useState<Step>("doc");
  const [docType, setDocType] = useState<DocType>("CC");
  const [docNumber, setDocNumber] = useState("");
  const [session, setSession] = useState<IdentityStartResponse | null>(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Manual fallback when DIAN has no record for the document.
  const [manualMode, setManualMode] = useState(false);
  const [manualName, setManualName] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [personName, setPersonName] = useState<PersonNameValue>(emptyPersonName());

  const phone = auth.status === "authenticated" ? auth.user.phone : "";

  const startSubmit = async () => {
    if (!docNumber.trim()) {
      setError(t("onb.err_doc_required"));
      return;
    }
    if (manualMode && (!manualEmail.trim() || (docType === "NIT" ? !manualName.trim() : !personName.firstName.trim() || !personName.firstSurname.trim()))) {
      setError("Ingresa tu nombre y correo.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await identityApi.start({
        documentType: docType,
        documentNumber: docNumber.trim(),
        ...(manualMode
          ? docType === "NIT"
            ? { fullName: manualName.trim(), email: manualEmail.trim() }
            : {
                firstName: personName.firstName.trim(),
                secondName: personName.secondName.trim() || undefined,
                firstSurname: personName.firstSurname.trim(),
                secondSurname: personName.secondSurname.trim() || undefined,
                email: manualEmail.trim(),
              }
          : {}),
      });
      setSession(result);
      setStep("code");
      toast.success(t("onb.email_sent"));
    } catch (err) {
      // DIAN has no record → switch to manual entry (name + email) and retry.
      if (err instanceof ApiError && err.code === "MANUAL_ENTRY_REQUIRED") {
        setManualMode(true);
        setError(
          docType === "NIT"
            ? "No encontramos tu documento en DIAN. Ingresa la razón social y el correo para continuar."
            : "No encontramos tu documento en DIAN. Ingresa tus nombres, apellidos y correo para continuar."
        );
      } else {
        const message = err instanceof Error ? err.message : t("onb.err_unknown");
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const verifySubmit = async () => {
    if (!session) return;
    if (!/^\d{6}$/.test(code)) {
      setError(t("onb.err_code_format"));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const profile = await identityApi.verify({
        sessionId: session.sessionId,
        code,
        ...(docType !== "NIT"
          ? {
              firstName: personName.firstName.trim(),
              secondName: personName.secondName.trim() || undefined,
              firstSurname: personName.firstSurname.trim(),
              secondSurname: personName.secondSurname.trim() || undefined,
            }
          : {}),
      });
      updateProfile({
        ...profileFromApi(profile),
        email: profile.email,
        documentType: profile.documentType === "PASAPORTE" ? "PA" : profile.documentType,
        documentNumber: profile.documentNumber,
        kind: profile.documentType === "NIT" ? "juridica" : "natural",
        companyName: profile.documentType === "NIT" ? profile.fullName : null,
      });
      setStep("done");
      toast.success(t("onb.welcome"));
      // Brief moment on the success screen, then unlock the app.
      setTimeout(() => onComplete(profile), 1100);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t("onb.err_unknown");
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setError(null);
    setLoading(true);
    try {
      const result = await identityApi.start({
        documentType: docType,
        documentNumber: docNumber.trim(),
        ...(manualMode
          ? docType === "NIT"
            ? { fullName: manualName.trim(), email: manualEmail.trim() }
            : {
                firstName: personName.firstName.trim(),
                secondName: personName.secondName.trim() || undefined,
                firstSurname: personName.firstSurname.trim(),
                secondSurname: personName.secondSurname.trim() || undefined,
                email: manualEmail.trim(),
              }
          : {}),
      });
      setSession(result);
      setCode("");
      toast.success(t("onb.email_resent"));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("onb.err_unknown"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-white to-brand-50">
      <div className="absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full bg-brand-200/40 blur-3xl" aria-hidden />
      <div className="absolute -left-20 bottom-0 h-[320px] w-[320px] rounded-full bg-brand-100/60 blur-3xl" aria-hidden />

      <header className="relative z-10 flex items-center justify-between px-6 pt-6 lg:px-10">
        <Logo />
        <div className="flex items-center gap-3">
          <LanguageSwitcher compact />
          <button
            type="button"
            onClick={() => auth.signOut()}
            className="text-xs font-medium text-slate-500 hover:text-slate-900"
          >
            {t("common.logout")}
          </button>
        </div>
      </header>

      <main className="relative z-10 mx-auto flex max-w-xl flex-col px-6 pb-16 pt-12 sm:pt-16">
        <Stepper step={step} />

        {step === "doc" && (
          <DocStep
            docType={docType}
            docNumber={docNumber}
            phone={phone}
            error={error}
            loading={loading}
            manualMode={manualMode}
            manualName={manualName}
            manualEmail={manualEmail}
            personName={personName}
            onChangeType={setDocType}
            onChangeNumber={setDocNumber}
            onChangeManualName={setManualName}
            onChangeManualEmail={setManualEmail}
            onChangePersonName={setPersonName}
            onSubmit={startSubmit}
          />
        )}

        {step === "code" && session && (
          <CodeStep
            session={session}
            code={code}
            error={error}
            loading={loading}
            isNatural={docType !== "NIT"}
            personName={personName}
            onChangePersonName={setPersonName}
            onChangeCode={setCode}
            onSubmit={verifySubmit}
            onResend={resend}
            onBack={() => {
              setStep("doc");
              setSession(null);
              setCode("");
              setError(null);
            }}
          />
        )}

        {step === "done" && <DoneStep />}
      </main>
    </div>
  );
}

function Stepper({ step }: { step: Step }) {
  const { t } = useT();
  const items: { id: Step; label: string }[] = [
    { id: "doc", label: t("onb.step_doc") },
    { id: "code", label: t("onb.step_code") },
  ];
  const active = step === "done" ? items.length : items.findIndex((s) => s.id === step);
  return (
    <ol className="mb-10 flex items-center gap-3">
      {items.map((item, i) => {
        const done = i < active;
        const current = i === active && step !== "done";
        return (
          <li key={item.id} className="flex flex-1 items-center gap-3">
            <span
              className={cn(
                "inline-flex h-8 w-8 flex-none items-center justify-center rounded-full text-xs font-semibold transition",
                done && "bg-emerald-600 text-white",
                current && "bg-brand-600 text-white shadow-pop",
                !done && !current && "bg-slate-200 text-slate-500"
              )}
            >
              {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
            </span>
            <span className={cn("text-xs font-medium", current ? "text-slate-900" : "text-slate-500")}>
              {item.label}
            </span>
            {i < items.length - 1 && <span className={cn("h-px flex-1", done ? "bg-emerald-500/70" : "bg-slate-200")} />}
          </li>
        );
      })}
    </ol>
  );
}

function DocStep({
  docType,
  docNumber,
  phone,
  error,
  loading,
  manualMode,
  manualName,
  manualEmail,
  personName,
  onChangeType,
  onChangeNumber,
  onChangeManualName,
  onChangeManualEmail,
  onChangePersonName,
  onSubmit,
}: {
  docType: DocType;
  docNumber: string;
  phone: string;
  error: string | null;
  loading: boolean;
  manualMode: boolean;
  manualName: string;
  manualEmail: string;
  personName: PersonNameValue;
  onChangeType: (t: DocType) => void;
  onChangeNumber: (v: string) => void;
  onChangeManualName: (v: string) => void;
  onChangeManualEmail: (v: string) => void;
  onChangePersonName: (v: PersonNameValue) => void;
  onSubmit: () => void;
}) {
  const { t } = useT();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <span className="inline-flex items-center gap-2 self-start rounded-full border border-brand-100 bg-white/80 px-3 py-1 text-xs font-medium text-brand-700 backdrop-blur">
        <ShieldCheck className="h-3.5 w-3.5" />
        {t("onb.secure_step")}
      </span>
      <h1 className="mt-6 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
        {t("onb.doc_title")}
      </h1>
      <p className="mt-3 text-base leading-relaxed text-slate-600">{t("onb.doc_subtitle")}</p>

      <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs text-slate-600">
        <span className="font-mono">+{phone}</span>
        <span className="text-slate-400">·</span>
        <span>{t("onb.phone_verified")}</span>
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
      </div>

      <div className="mt-8 space-y-5">
        <fieldset>
          <legend className="label">{t("onb.doc_type")}</legend>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {DOC_TYPES.map((dt) => {
              const active = docType === dt.id;
              return (
                <button
                  key={dt.id}
                  type="button"
                  onClick={() => onChangeType(dt.id)}
                  className={cn(
                    "flex items-start gap-3 rounded-xl border p-3 text-left transition",
                    active
                      ? "border-brand-600 bg-brand-50/60 shadow-pop"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  )}
                  aria-pressed={active}
                >
                  <span
                    className={cn(
                      "inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg",
                      active ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-600"
                    )}
                  >
                    <IdCard className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{dt.label}</p>
                    <p className="text-xs text-slate-500">{dt.helper}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </fieldset>

        <label className="block">
          <span className="label">{t("onb.doc_number")}</span>
          <input
            className="input mt-1.5"
            value={docNumber}
            onChange={(e) => onChangeNumber(e.target.value.trim())}
            inputMode={docType === "PASAPORTE" ? "text" : "numeric"}
            placeholder={docType === "PASAPORTE" ? "AB123456" : docType === "NIT" ? "901234567" : "1045678900"}
            autoComplete="off"
            autoFocus
            required
          />
          <p className="mt-2 text-xs text-slate-500">{t("onb.doc_helper")}</p>
        </label>

        {manualMode && (
          <div className="space-y-4 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
            <p className="text-xs text-amber-800">
              No encontramos tu documento en DIAN. Ingresa {docType === "NIT" ? "la razón social" : "tus nombres y apellidos"} y correo; te
              enviaremos un código para verificarlo.
            </p>
            {docType === "NIT" ? (
              <label className="block">
                <span className="label">Razón social</span>
                <input
                  className="input mt-1.5"
                  value={manualName}
                  onChange={(e) => onChangeManualName(e.target.value)}
                  placeholder="Como aparece en el RUT"
                  autoComplete="organization"
                  required
                />
              </label>
            ) : (
              <PersonNameFields value={personName} onChange={onChangePersonName} />
            )}
            <label className="block">
              <span className="label">Correo electrónico</span>
              <input
                className="input mt-1.5"
                type="email"
                value={manualEmail}
                onChange={(e) => onChangeManualEmail(e.target.value.trim())}
                placeholder="tu@correo.com"
                autoComplete="email"
                required
              />
            </label>
          </div>
        )}

        {error && (
          <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <Button type="submit" size="lg" block loading={loading}>
          {t("onb.continue")}
        </Button>

        <p className="text-center text-xs text-slate-500">{t("onb.privacy_note")}</p>
      </div>
    </form>
  );
}

function CodeStep({
  session,
  code,
  error,
  loading,
  isNatural,
  personName,
  onChangePersonName,
  onChangeCode,
  onSubmit,
  onResend,
  onBack,
}: {
  session: IdentityStartResponse;
  code: string;
  error: string | null;
  loading: boolean;
  isNatural: boolean;
  personName: PersonNameValue;
  onChangePersonName: (v: PersonNameValue) => void;
  onChangeCode: (v: string) => void;
  onSubmit: () => void;
  onResend: () => void;
  onBack: () => void;
}) {
  const { t } = useT();
  const expiresAt = useMemo(() => Date.now() + session.expiresInSeconds * 1000, [session]);
  const [remaining, setRemaining] = useState(session.expiresInSeconds);
  useEffect(() => {
    const id = setInterval(() => {
      setRemaining(Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <span className="inline-flex items-center gap-2 self-start rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
        <Mail className="h-3.5 w-3.5" />
        {t("onb.email_sent_chip")}
      </span>
      <h1 className="mt-6 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
        {t("onb.greeting", { name: session.maskedName })}
      </h1>
      <p className="mt-3 text-base leading-relaxed text-slate-600">
        {t("onb.code_subtitle")}{" "}
        <span className="font-medium text-slate-900">{session.maskedEmail}</span>.
      </p>

      <div className="mt-8 space-y-5">
        {isNatural && (
          <PersonNameFields
            value={personName}
            onChange={onChangePersonName}
            hint={session.dianName ?? session.maskedName}
          />
        )}

        <label className="block">
          <span className="label flex items-center justify-between">
            <span>{t("onb.code_label")}</span>
            <span className="font-mono text-xs text-slate-500">{mm}:{ss}</span>
          </span>
          <input
            className="input mt-2 text-center text-2xl font-semibold tracking-[0.5em] tabular-nums"
            value={code}
            onChange={(e) => onChangeCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="••••••"
            autoFocus
            required
          />
        </label>

        {error && (
          <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <Button type="submit" size="lg" block loading={loading} disabled={code.length !== 6 || (isNatural && (!personName.firstName.trim() || !personName.firstSurname.trim()))}>
          <KeyRound className="h-4 w-4" />
          {t("onb.verify")}
        </Button>

        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={onResend}
            disabled={loading || remaining > session.expiresInSeconds - 30}
            className="inline-flex items-center gap-1.5 font-medium text-brand-700 hover:text-brand-800 disabled:opacity-50"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t("onb.resend")}
          </button>
          <button
            type="button"
            onClick={onBack}
            className="font-medium text-slate-500 hover:text-slate-900"
          >
            {t("onb.change_doc")}
          </button>
        </div>
      </div>
    </form>
  );
}

function DoneStep() {
  const { t } = useT();
  return (
    <div className="flex flex-col items-center text-center">
      <div className="inline-flex h-16 w-16 items-center justify-center rounded-3xl bg-emerald-100 text-emerald-700">
        <Loader2 className="h-7 w-7 animate-spin" />
      </div>
      <h1 className="mt-6 text-2xl font-semibold tracking-tight text-slate-900">
        {t("onb.welcome")}
      </h1>
      <p className="mt-2 text-sm text-slate-600">{t("onb.unlocking")}</p>
    </div>
  );
}
